import { createElement } from "react";
import { EditorView } from "prosemirror-view";
import { createRoot } from "react-dom/client";
import tippy, { Instance as TippyInstance } from "tippy.js";
import { Suggestion, SuggestionState } from "./types";
import { suggestionPluginKey } from "./plugin";
import { TokenSuggestionPopup } from "./TokenSuggestionPopup";

export type SuggestionPopupRenderer = {
    container: HTMLDivElement;
    destroy: () => void;
    render: (state: SuggestionState, insertCallback: (suggestion: Suggestion) => void) => void;
};

const getPopupTitle = (state: SuggestionState) => {
    if (!state.isNamespaceSelected) {
        return "Token namespaces";
    }

    const query = state.query || "";
    const justTypedDot = query.endsWith(".");

    if (justTypedDot) {
        return `${
            query
                .substring(0, query.length - 1)
                .split(".")
                .pop() || ""
        } Properties`;
    }

    const queryParts = query.split(".");
    const parentSegment = queryParts.length > 1 ? (queryParts.at(-2) ?? "") : queryParts[0];
    return `${parentSegment} Properties`;
};

export const createPopupRenderer = (
    state: SuggestionState,
    insertCallback: (suggestion: Suggestion) => void,
): SuggestionPopupRenderer => {
    const container = document.createElement("div");
    const root = createRoot(container);

    const render = (nextState: SuggestionState, nextInsertCallback: (suggestion: Suggestion) => void) => {
        root.render(
            createElement(TokenSuggestionPopup, {
                title: getPopupTitle(nextState),
                suggestions: nextState.suggestions,
                selectedIndex: nextState.selectedIndex,
                insertCallback: nextInsertCallback,
            }),
        );
    };

    render(state, insertCallback);

    return {
        container,
        destroy: () => {
            root.unmount();
        },
        render,
    };
};

export const createPopupReferenceClientRect = (view: EditorView, from: number) => {
    const coords = view.coordsAtPos(from);

    return () => {
        const domSelection = globalThis.getSelection();

        if (domSelection && domSelection.rangeCount > 0) {
            const range = domSelection.getRangeAt(0);
            const rect = range.getBoundingClientRect();

            if (rect && rect.width >= 0 && rect.height >= 0) {
                return {
                    width: 0,
                    height: 0,
                    top: rect.bottom,
                    bottom: rect.bottom + 5,
                    left: rect.left,
                    right: rect.right,
                    x: rect.left,
                    y: rect.bottom,
                    toJSON: () => {},
                };
            }
        }

        return {
            width: 0,
            height: 0,
            top: coords.bottom,
            bottom: coords.bottom + 5,
            left: coords.left,
            right: coords.right,
            x: coords.left,
            y: coords.bottom,
            toJSON: () => {},
        };
    };
};

export const showSuggestionsPopup = (view: EditorView, from: number, container: HTMLDivElement): TippyInstance => {
    const editorElement = view.dom;

    return tippy(editorElement, {
        content: container,
        showOnCreate: true,
        interactive: true,
        trigger: "manual",
        placement: "bottom",
        theme: "light-border",
        arrow: true,
        getReferenceClientRect: createPopupReferenceClientRect(view, from),
        onHide: () => {
            const state = suggestionPluginKey.getState(view.state);
            if (state?.active) {
                view.dispatch(view.state.tr.setMeta("token-suggestion-hide", true));
            }
        },
    });
};
